// The three effects. Each has an input, an output and a 0..1 dry/wet mix,
// plus two "character" parameters normalized to 0..1.

export interface Effect {
  readonly input: GainNode;
  readonly output: GainNode;
  setMix(mix: number): void;
  setParam(name: string, value: number): void;
}

type MixLaw = 'linear' | 'equal-power';

/** Dry/wet crossfade. Equal-power for decorrelated wet signals (reverb,
 *  grains) so the middle doesn't dip; linear for correlated ones. */
class DryWet {
  readonly input: GainNode;
  readonly output: GainNode;
  readonly wetIn: GainNode;
  readonly wetOut: GainNode;
  private dry: GainNode;
  private wet: GainNode;

  constructor(
    private ctx: AudioContext,
    private law: MixLaw,
    /** Optional node on the dry path, e.g. to match the wet path's latency. */
    dryInsert?: AudioNode,
  ) {
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.dry = ctx.createGain();
    this.wet = ctx.createGain();
    this.wetIn = ctx.createGain();
    this.wetOut = ctx.createGain();
    if (dryInsert) this.input.connect(dryInsert).connect(this.dry).connect(this.output);
    else this.input.connect(this.dry).connect(this.output);
    this.input.connect(this.wetIn);
    this.wetOut.connect(this.wet).connect(this.output);
    this.setMix(0);
  }

  setMix(mix: number): void {
    const m = Math.min(1, Math.max(0, mix));
    const [d, w] = this.law === 'linear' ? [1 - m, m] : [Math.cos((m * Math.PI) / 2), Math.sin((m * Math.PI) / 2)];
    const t = this.ctx.currentTime;
    this.dry.gain.setTargetAtTime(d, t, 0.02);
    this.wet.gain.setTargetAtTime(w, t, 0.02);
  }
}

// ---------------------------------------------------------------- Reverb

export interface ReverbShape {
  /** Seconds to decay by 60 dB. */
  decay: number;
  /** Cutoff in Hz that the tail darkens towards. */
  damping: number;
  preDelay: number;
}

export const reverbShape = (size: number, tone: number): ReverbShape => ({
  decay: 0.5 + Math.pow(size, 1.6) * 9.5,
  damping: 1200 * Math.pow(14, tone),
  preDelay: 0.004 + size * 0.03,
});

/**
 * Render a stereo impulse response offline: decorrelated noise with an
 * exponential decay, a cluster of early reflections, and a lowpass that
 * closes over time so the tail gets darker the way real rooms do.
 */
export async function renderImpulse(sampleRate: number, shape: ReverbShape): Promise<AudioBuffer> {
  const length = Math.ceil(sampleRate * (shape.decay + 0.2));
  const offline = new OfflineAudioContext(2, length, sampleRate);
  const noise = offline.createBuffer(2, length, sampleRate);
  const k = Math.log(1000) / shape.decay; // -60 dB at `decay`
  for (let c = 0; c < 2; c++) {
    const d = noise.getChannelData(c);
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const fadeIn = Math.min(1, t / 0.012);
      d[i] = (Math.random() * 2 - 1) * Math.exp(-k * t) * fadeIn;
    }
    // Early reflections: sparse taps in the first ~80 ms, alternating sides.
    for (let r = 0; r < 14; r++) {
      const t = 0.006 + Math.random() * 0.075;
      const i = Math.floor(t * sampleRate);
      if (i < length) d[i] += (Math.random() < 0.5 ? -1 : 1) * (0.9 - r * 0.05) * (c === r % 2 ? 1 : 0.55);
    }
  }
  const src = offline.createBufferSource();
  src.buffer = noise;
  const lp = offline.createBiquadFilter();
  lp.type = 'lowpass';
  lp.Q.value = 0.5;
  lp.frequency.setValueAtTime(Math.min(18000, shape.damping * 2.2), 0);
  lp.frequency.exponentialRampToValueAtTime(Math.max(300, shape.damping * 0.35), shape.decay);
  const hp = offline.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 110;
  src.connect(lp).connect(hp).connect(offline.destination);
  src.start();
  const out = await offline.startRendering();
  // Normalize so every size sits at a similar perceived level.
  let energy = 0;
  for (let c = 0; c < 2; c++) {
    const d = out.getChannelData(c);
    for (let i = 0; i < d.length; i++) energy += d[i] * d[i];
  }
  const norm = 0.9 / Math.sqrt(energy / 2);
  for (let c = 0; c < 2; c++) {
    const d = out.getChannelData(c);
    for (let i = 0; i < d.length; i++) d[i] *= norm;
  }
  return out;
}

export class Reverb implements Effect {
  readonly input: GainNode;
  readonly output: GainNode;
  private dw: DryWet;
  private pre: DelayNode;
  private slots: { conv: ConvolverNode; gain: GainNode }[];
  private active = 0;
  private size = 0.45;
  private tone = 0.55;
  private pending = 0;
  private generation = 0;

  constructor(private ctx: AudioContext) {
    this.dw = new DryWet(ctx, 'equal-power');
    this.input = this.dw.input;
    this.output = this.dw.output;
    this.pre = ctx.createDelay(0.2);
    this.dw.wetIn.connect(this.pre);
    // Two convolvers so a new impulse can crossfade in without a click.
    this.slots = [0, 1].map(() => {
      const conv = ctx.createConvolver();
      conv.normalize = false;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      this.pre.connect(conv).connect(gain).connect(this.dw.wetOut);
      return { conv, gain };
    });
    void this.rebuild();
  }

  setMix(mix: number): void {
    this.dw.setMix(mix);
  }

  setParam(name: string, value: number): void {
    if (name === 'size') this.size = value;
    else if (name === 'tone') this.tone = value;
    else return;
    clearTimeout(this.pending);
    this.pending = window.setTimeout(() => void this.rebuild(), 120);
  }

  private async rebuild(): Promise<void> {
    const gen = ++this.generation;
    const shape = reverbShape(this.size, this.tone);
    const ir = await renderImpulse(this.ctx.sampleRate, shape);
    if (gen !== this.generation) return;
    const next = 1 - this.active;
    const t = this.ctx.currentTime;
    this.slots[next].conv.buffer = ir;
    this.pre.delayTime.setTargetAtTime(shape.preDelay, t, 0.05);
    this.slots[next].gain.gain.setTargetAtTime(1, t, 0.08);
    this.slots[this.active].gain.gain.setTargetAtTime(0, t, 0.08);
    this.active = next;
  }
}

// ------------------------------------------------------------ Saturation

export function saturationCurve(drive: number, samples = 4096): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(samples);
  const k = 1 + drive * 24;
  // A small bias makes the curve asymmetric, adding even harmonics (warmth).
  const bias = 0.12 * drive;
  const norm = Math.tanh(k * (1 + bias)) - Math.tanh(k * bias);
  const normNeg = Math.tanh(k * bias) - Math.tanh(k * (bias - 1));
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const y = Math.tanh(k * (x + bias)) - Math.tanh(k * bias);
    curve[i] = y / (y >= 0 ? norm : normNeg);
  }
  return curve;
}

/** Gain that matches the RMS of a typical-level sine before and after the curve. */
export function autoGain(curve: Float32Array, pre: number, amplitude = 0.18): number {
  const n = curve.length - 1;
  let inSum = 0;
  let outSum = 0;
  for (let i = 0; i < 256; i++) {
    const x = amplitude * Math.sin((i / 256) * Math.PI * 2);
    const xi = Math.max(-1, Math.min(1, x * pre));
    const pos = ((xi + 1) / 2) * n;
    const i0 = Math.floor(pos);
    const y = curve[i0] + (curve[Math.min(n, i0 + 1)] - curve[i0]) * (pos - i0);
    inSum += x * x;
    outSum += y * y;
  }
  return Math.sqrt(inSum / Math.max(1e-9, outSum));
}

/** Transparent below -1.4 dBFS, then rounds peaks off instead of hard clipping. */
export function safetyCurve(samples = 2048): Float32Array<ArrayBuffer> {
  const c = new Float32Array(samples);
  const knee = 0.85;
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee));
    c[i] = Math.sign(x) * y * 0.99;
  }
  return c;
}

export class Saturation implements Effect {
  readonly input: GainNode;
  readonly output: GainNode;
  private dw: DryWet;
  private shaper: WaveShaperNode;
  private tone: BiquadFilterNode;
  private makeup: GainNode;
  private pre: GainNode;

  constructor(private ctx: AudioContext) {
    // Oversampling delays the wet signal slightly. Send the dry signal through
    // an identical, perfectly linear shaper so the two stay phase-aligned and
    // blending them doesn't comb-filter.
    const align = ctx.createWaveShaper();
    align.curve = new Float32Array([-1, 1]);
    align.oversample = '4x';
    this.dw = new DryWet(ctx, 'linear', align);
    this.input = this.dw.input;
    this.output = this.dw.output;
    this.pre = ctx.createGain();
    this.shaper = ctx.createWaveShaper();
    this.shaper.oversample = '4x';
    this.tone = ctx.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.Q.value = 0.5;
    this.makeup = ctx.createGain();
    this.dw.wetIn.connect(this.pre).connect(this.shaper).connect(this.tone).connect(this.makeup).connect(this.dw.wetOut);
    this.setParam('drive', 0.4);
    this.setParam('tone', 0.7);
  }

  setMix(mix: number): void {
    this.dw.setMix(mix);
  }

  setParam(name: string, value: number): void {
    const t = this.ctx.currentTime;
    if (name === 'drive') {
      const curve = saturationCurve(value);
      const pre = 1 + value * 1.5;
      this.shaper.curve = curve;
      this.pre.gain.setTargetAtTime(pre, t, 0.02);
      // Loudness compensation so turning up drive adds grit, not volume.
      this.makeup.gain.setTargetAtTime(autoGain(curve, pre), t, 0.02);
    } else if (name === 'tone') {
      this.tone.frequency.setTargetAtTime(1500 * Math.pow(12, value), t, 0.02);
    }
  }
}

// -------------------------------------------------------------- Granular

export const GRAIN_PITCHES = [-12, -7, 0, 7, 12];

export const grainPitch = (value: number): number =>
  GRAIN_PITCHES[Math.min(GRAIN_PITCHES.length - 1, Math.round(value * (GRAIN_PITCHES.length - 1)))];

export const grainSize = (value: number): number => 0.025 + Math.pow(value, 1.5) * 0.35;

export class Granular implements Effect {
  readonly input: GainNode;
  readonly output: GainNode;
  private dw: DryWet;
  private node: AudioWorkletNode;

  constructor(private ctx: AudioContext) {
    this.dw = new DryWet(ctx, 'equal-power');
    this.input = this.dw.input;
    this.output = this.dw.output;
    this.node = new AudioWorkletNode(ctx, 'spiel-granular', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    const lift = ctx.createGain();
    lift.gain.value = 1.9;
    this.dw.wetIn.connect(this.node).connect(lift).connect(this.dw.wetOut);
  }

  setMix(mix: number): void {
    this.dw.setMix(mix);
  }

  setParam(name: string, value: number): void {
    const t = this.ctx.currentTime;
    if (name === 'size') this.node.parameters.get('size')!.setValueAtTime(grainSize(value), t);
    else if (name === 'pitch') this.node.parameters.get('pitch')!.setValueAtTime(grainPitch(value), t);
    else if (name === 'spray') this.node.parameters.get('spray')!.setValueAtTime(value, t);
  }
}
